import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { getUserClient, UserPaths } from '@/api/user-client'
import { IUser } from '@/models'
import { UserTableColumns } from '@/modules/user/columns'
import { useRoleAndPermission } from '@/providers'
import { useQueryGetOrganizations, USER_QUERY_IDS } from '@/services'

export type UserSortBy =
  | 'user_id'
  | 'name'
  | 'email'
  | 'status'
  | 'org_id'
  | 'created_at'
  | 'last_login_at'
  | 'last_seen_at'

type Props = {
  orgId?: string
  limit?: number
  page?: number
  search?: string
  sortBy?: UserSortBy
  order?: 'asc' | 'desc'
  enabled?: boolean
  fetchAll?: boolean
}

export const useUserTable = ({
  orgId,
  limit,
  page,
  search,
  sortBy,
  order,
  enabled = true,
  fetchAll = false
}: Props) => {
  const userClient = getUserClient()

  const queryLimit = fetchAll ? 1000 : limit
  const queryPage = fetchAll ? undefined : page

  const { data, isFetched } = useQuery<UserPaths.GetUsersV2.Responses.$200>({
    queryKey: [
      USER_QUERY_IDS.GET_USERS,
      orgId,
      queryLimit,
      queryPage,
      search,
      sortBy,
      order,
      fetchAll
    ],
    queryFn: async () => {
      // sort_by / order exist in the API but not yet in the published client types
      const sortParams = (sortBy ? { sort_by: sortBy, order } : {}) as object

      if (fetchAll) {
        let users: UserPaths.GetUsersV2.Responses.$200['results'] = []
        let currentPage = 1

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const response = await userClient.getUsersV2({
            org_id: orgId,
            limit: queryLimit,
            page: currentPage,
            search,
            ...sortParams
          })

          users = users.concat(response.data.results!)

          if (currentPage >= response.data.total_pages!) break

          currentPage++
        }

        return {
          results: users,
          total_count: users.length,
          page: 1,
          total_pages: 1
        }
      } else
        return userClient
          .getUsersV2({
            org_id: orgId,
            limit: queryLimit,
            page: queryPage,
            search,
            ...sortParams
          })
          .then((res) => res.data)
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    enabled
  })

  const { data: orgsQueryResp, isFetched: isOrgsFetched } =
    useQueryGetOrganizations()

  const {
    allAssignmentsAllOrgs,
    allRolesAllOrgs,
    isReadyExport,
    loadingGetAllRolesOrgIds,
    loadingGetAssignedRoleUsers
  } = useRoleAndPermission()

  const isReady =
    isFetched &&
    isOrgsFetched &&
    !loadingGetAllRolesOrgIds &&
    !loadingGetAssignedRoleUsers

  const users = useMemo(() => {
    if (!isReady) return []

    const raw = data?.results ?? []

    const transformed = raw.map((user) => {
      const orgAssignments =
        allAssignmentsAllOrgs?.find(
          (a) => a.organizationId === user.organization_id
        )?.assignments ?? []

      const userAssignments = orgAssignments.find((a) => a.userId === user.id)

      const orgRoles =
        allRolesAllOrgs?.find((r) => r.organizationId === user.organization_id)
          ?.roles ?? []

      const userRoles = orgRoles.filter((role) =>
        userAssignments?.roles?.includes(role.id)
      )

      return {
        ...user,
        organization:
          orgsQueryResp?.find((o) => o.id === user.organization_id) ?? null,
        roles: userRoles
      }
    })

    return (
      orgId
        ? transformed.filter((u) => u.organization_id === orgId)
        : transformed
    ) as IUser[]
  }, [
    isReady,
    data,
    orgId,
    orgsQueryResp,
    allAssignmentsAllOrgs,
    allRolesAllOrgs
  ])

  return {
    data: users,
    totalCount: data?.total_count,
    page: data?.page,
    totalPages: data?.total_pages,
    isLoading: !isReady,
    isRolesLoading: loadingGetAllRolesOrgIds || loadingGetAssignedRoleUsers,
    columns: UserTableColumns,
    isReadyExport
  }
}
